import React, { useState, useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import { motion } from 'framer-motion'
import { Upload, FileText, Download, X, File, Image, Archive } from 'lucide-react'
import toast from 'react-hot-toast'
import * as pdfjsLib from 'pdfjs-dist'
import AdContainer from '../components/ads/AdContainer'
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig'

// Configure PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`

interface ConversionOptions {
  quality: number
  resolution: '72' | '150' | '300' | '600'
  format: 'JPG' | 'JPEG'
  colorSpace: 'RGB' | 'CMYK' | 'Grayscale'
  pages: 'all' | 'range' | 'specific'
  pageRange?: string
  specificPages?: string
}

interface ProcessedFile {
  id: string
  originalFile: File
  name: string
  size: number
  status: 'pending' | 'processing' | 'completed' | 'error'
  progress: number
  images?: Blob[]
  zipBlob?: Blob
  pageCount?: number
}

const PdfToJpg: React.FC = () => {
  const [files, setFiles] = useState<ProcessedFile[]>([])
  const [options, setOptions] = useState<ConversionOptions>({
    quality: 90,
    resolution: '150',
    format: 'JPG',
    colorSpace: 'RGB',
    pages: 'all',
    pageRange: '1-10',
    specificPages: '1,3,5'
  })
  const [isProcessing, setIsProcessing] = useState(false)

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const pdfFiles = acceptedFiles.filter(file => 
      file.type === 'application/pdf' ||
      file.name.toLowerCase().endsWith('.pdf')
    )

    if (pdfFiles.length === 0) {
      toast.error('Please select valid PDF files')
      return
    }

    const newFiles: ProcessedFile[] = pdfFiles.map(file => ({
      id: Math.random().toString(36).substr(2, 9),
      originalFile: file,
      name: file.name,
      size: file.size,
      status: 'pending',
      progress: 0,
      pageCount: Math.floor(Math.random() * 20) + 1 // Simulated page count
    }))

    setFiles(prev => [...prev, ...newFiles])
    toast.success(`Added ${pdfFiles.length} PDF file(s)`)
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf']
    },
    multiple: true
  })

  const processFiles = async () => {
    if (files.length === 0) return

    setIsProcessing(true)
    
    for (const file of files.filter(f => f.status === 'pending')) {
      try {
        setFiles(prev => prev.map(f => 
          f.id === file.id ? { ...f, status: 'processing', progress: 10 } : f
        ))

        // Read PDF file
        const arrayBuffer = await file.originalFile.arrayBuffer()
        const loadingTask = pdfjsLib.getDocument(arrayBuffer)
        const pdfDoc = await loadingTask.promise
        
        setFiles(prev => prev.map(f => 
          f.id === file.id ? { ...f, progress: 30 } : f
        ))

        const pageCount = pdfDoc.numPages
        const images: Blob[] = []
        
        setFiles(prev => prev.map(f => 
          f.id === file.id ? { ...f, progress: 50 } : f
        ))

        // Convert each page to image
        for (let pageNum = 1; pageNum <= pageCount; pageNum++) {
          const page = await pdfDoc.getPage(pageNum)
          const scale = parseInt(options.resolution) / 72 // Convert DPI to scale factor
          const viewport = page.getViewport({ scale })

          // Create canvas
          const canvas = document.createElement('canvas')
          const context = canvas.getContext('2d')
          canvas.height = viewport.height
          canvas.width = viewport.width

          if (context) {
            // Render page to canvas
            const renderContext = {
              canvasContext: context,
              viewport: viewport,
              canvas: canvas
            }
            
            await page.render(renderContext).promise

            // Convert canvas to blob
            await new Promise<void>((resolve) => {
              canvas.toBlob((blob) => {
                if (blob) {
                  images.push(blob)
                }
                resolve()
              }, 'image/jpeg', options.quality / 100)
            })
          }

          setFiles(prev => prev.map(f => 
            f.id === file.id ? { ...f, progress: 50 + (35 * pageNum / pageCount) } : f
          ))
        }

        // Create a simple ZIP-like structure (placeholder)
        const zipContent = new Uint8Array([0x50, 0x4B, 0x03, 0x04]) // ZIP signature
        const zipBlob = new Blob([zipContent], { type: 'application/zip' })

        setFiles(prev => prev.map(f => 
          f.id === file.id 
            ? { ...f, status: 'completed', progress: 100, images, zipBlob }
            : f
        ))

      } catch (error) {
        console.error('Conversion error:', error)
        setFiles(prev => prev.map(f => 
          f.id === file.id ? { ...f, status: 'error', progress: 0 } : f
        ))
        toast.error(`Failed to convert ${file.name}`)
      }
    }

    setIsProcessing(false)
    toast.success('PDF to JPG conversion completed!')
  }

  const downloadZip = (file: ProcessedFile) => {
    if (!file.zipBlob) return

    const url = URL.createObjectURL(file.zipBlob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${file.name.replace(/\.[^/.]+$/, '')}_images.zip`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const downloadSingleImage = (file: ProcessedFile, imageIndex: number) => {
    if (!file.images || !file.images[imageIndex]) return

    const url = URL.createObjectURL(file.images[imageIndex])
    const a = document.createElement('a')
    a.href = url
    a.download = `${file.name.replace(/\.[^/.]+$/, '')}_page_${imageIndex + 1}.jpg`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const removeFile = (id: string) => {
    setFiles(prev => prev.filter(f => f.id !== id))
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-[#8438ff] to-[#7228ee] rounded-2xl mb-6">
            <Image className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-[#8438ff] mb-4">
            PDF to JPG Converter
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Convert PDF pages to high-quality JPG images with customizable resolution and quality settings.
          </p>
        </motion.div>

        {/* Header Ad */}
        <AdContainer 
          position="header" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.pdfToJpg?.header || '1234567890'}
          className="mb-8"
        />

        {/* Conversion Options */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card mb-8"
        >
          <div className="flex items-center space-x-2 mb-6">
            <Image className="h-5 w-5 text-[#8438ff]" />
            <h2 className="text-lg font-semibold text-[#8438ff]">Conversion Settings</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Quality: {options.quality}%
                </label>
                <input
                  type="range"
                  min="50"
                  max="100"
                  value={options.quality}
                  onChange={(e) => setOptions(prev => ({ ...prev, quality: parseInt(e.target.value) }))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                  style={{
                    background: `linear-gradient(to right, #8438ff 0%, #8438ff ${((options.quality - 50) / (100 - 50)) * 100}%, #e5e7eb ${((options.quality - 50) / (100 - 50)) * 100}%, #e5e7eb 100%)`
                  }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Resolution (DPI)
                </label>
                <select
                  value={options.resolution}
                  onChange={(e) => setOptions(prev => ({ ...prev, resolution: e.target.value as any }))}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8438ff] focus:border-transparent"
                >
                  <option value="72">72 DPI (Web)</option>
                  <option value="150">150 DPI (Standard)</option>
                  <option value="300">300 DPI (High Quality)</option>
                  <option value="600">600 DPI (Print Quality)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Color Space
                </label>
                <select
                  value={options.colorSpace}
                  onChange={(e) => setOptions(prev => ({ ...prev, colorSpace: e.target.value as any }))}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8438ff] focus:border-transparent"
                >
                  <option value="RGB">RGB (Color)</option>
                  <option value="CMYK">CMYK (Print)</option>
                  <option value="Grayscale">Grayscale</option>
                </select>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Pages to Convert
                </label>
                <select
                  value={options.pages}
                  onChange={(e) => setOptions(prev => ({ ...prev, pages: e.target.value as any }))}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8438ff] focus:border-transparent"
                >
                  <option value="all">All pages</option>
                  <option value="range">Page range</option>
                  <option value="specific">Specific pages</option>
                </select>
              </div>

              {options.pages === 'range' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Page Range (e.g., 1-5)
                  </label>
                  <input
                    type="text"
                    value={options.pageRange}
                    onChange={(e) => setOptions(prev => ({ ...prev, pageRange: e.target.value }))}
                    placeholder="1-10"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8438ff] focus:border-transparent"
                  />
                </div>
              )}

              {options.pages === 'specific' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Specific Pages (e.g., 1,3,5)
                  </label>
                  <input
                    type="text"
                    value={options.specificPages}
                    onChange={(e) => setOptions(prev => ({ ...prev, specificPages: e.target.value }))}
                    placeholder="1,3,5,7"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8438ff] focus:border-transparent"
                  />
                </div>
              )}
            </div>
          </div>
        </motion.div>

        {/* Upload Zone */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className={`upload-zone ${isDragActive ? 'active' : ''} mb-8`}
          {...getRootProps()}
        >
          <input {...getInputProps()} />
          <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-lg font-medium text-[#8438ff] mb-2">
            {isDragActive ? 'Drop your PDF files here' : 'Drag & drop PDF files here'}
          </p>
          <p className="text-gray-600 mb-4">
            or <span className="text-[#8438ff] font-medium">browse files</span>
          </p>
          <p className="text-sm text-gray-500">
            Supports PDF • Max 100MB per file
          </p>
        </motion.div>

        {/* Content Ad - appears when files are uploaded */}
        {files.length > 0 && (
          <AdContainer 
            position="content" 
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.pdfToJpg?.content || '0987654321'}
            className="mb-8"
          />
        )}

        {/* File List */}
        {files.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="card mb-8"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-[#8438ff]">
                Files ({files.length})
              </h3>
              <button
                onClick={processFiles}
                disabled={isProcessing || !files.some(f => f.status === 'pending')}
                className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isProcessing ? 'Converting...' : 'Convert to JPG'}
              </button>
            </div>

            <div className="space-y-4">
              {files.map((file) => (
                <div key={file.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      <FileText className="h-5 w-5 text-red-500" />
                      <div>
                        <p className="font-medium text-gray-900">{file.name}</p>
                        <p className="text-sm text-gray-500">
                          {formatFileSize(file.size)} • {file.pageCount} pages
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {file.status === 'completed' && (
                        <>
                          <button
                            onClick={() => downloadZip(file)}
                            className="btn btn-secondary text-sm"
                          >
                            <Archive className="h-4 w-4 mr-1" />
                            Download ZIP
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => removeFile(file.id)}
                        className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                        disabled={file.status === 'processing'}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  
                  {file.status !== 'pending' && (
                    <div className="mt-3">
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className={`font-medium ${
                          file.status === 'completed' ? 'text-green-600' : 
                          file.status === 'error' ? 'text-red-600' : 'text-blue-600'
                        }`}>
                          {file.status === 'processing' ? 'Converting pages...' : 
                           file.status === 'completed' ? 'Conversion completed' : 
                           file.status === 'error' ? 'Conversion failed' : ''}
                        </span>
                        <span className="text-gray-500">{file.progress}%</span>
                      </div>
                      <div className="progress-bar">
                        <div 
                          className="progress-fill"
                          style={{ width: `${file.progress}%` }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Individual page downloads */}
                  {file.status === 'completed' && file.images && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <p className="text-sm font-medium text-gray-700 mb-3">
                        Individual Pages ({file.images.length}):
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {file.images.slice(0, 10).map((_, index) => (
                          <button
                            key={index}
                            onClick={() => downloadSingleImage(file, index)}
                            className="px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                          >
                            Page {index + 1}
                          </button>
                        ))}
                        {file.images.length > 10 && (
                          <span className="px-3 py-1 text-xs text-gray-500">
                            +{file.images.length - 10} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Footer Ad */}
        <AdContainer 
          position="footer" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.pdfToJpg?.footer || '1122334455'}
          className="mt-12"
        />
      </div>
    </div>
  )
}

export default PdfToJpg
